import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { alpacaAPI } from '../services/api';
import { WelcomeModal } from './Onboarding';
import {
  LayoutGrid, BrainCircuit, Briefcase, Target, Search, Layers, Bitcoin,
  Zap, BarChart3, Activity, ShieldCheck, Compass, Newspaper, Award, Trophy,
  Bot, BookOpen, Settings, LogOut, RefreshCw, TrendingUp, Shield, LifeBuoy,
} from 'lucide-react';

const money = (n) => (n == null || isNaN(n) ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

// A compact status pill for the top bar (colored dot + label).
function StatusChip({ color = 'muted', pulse = false, label, title }) {
  const dot = { green: 'bg-accent-green', red: 'bg-accent-red', amber: 'bg-accent-yellow', muted: 'bg-text-muted' }[color] || 'bg-text-muted';
  const text = { green: 'text-accent-green', red: 'text-accent-red', amber: 'text-accent-yellow', muted: 'text-text-muted' }[color] || 'text-text-muted';
  return (
    <div title={title || label} className="flex items-center gap-1.5 px-2.5 h-[35px] rounded-[9px] border border-border-color bg-bg-secondary">
      <span className={`w-2 h-2 rounded-full ${dot} ${pulse ? 'animate-pulse' : ''}`} />
      <span className={`text-[12px] font-medium whitespace-nowrap ${text}`}>{label}</span>
    </div>
  );
}

// A compact metric pill (label + value), used for buying power / equity.
function MetricChip({ label, value, valueClass = 'text-text-primary' }) {
  return (
    <div className="hidden lg:flex items-center gap-1.5 px-2.5 h-[35px] rounded-[9px] border border-border-color bg-bg-secondary">
      <span className="text-[10px] font-mono uppercase tracking-wide text-text-muted">{label}</span>
      <span className={`text-[12.5px] font-semibold font-mono ${valueClass}`}>{value}</span>
    </div>
  );
}

// Grouped navigation — the persistent shell so every page has nav (no dead-ends).
const NAV = [
  { group: 'Overview', items: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid, perm: 'nav.dashboard' },
    { path: '/war-room', label: 'War Room', icon: BrainCircuit, tag: 'AI', tagCls: 'text-accent-teal bg-accent-teal/10', perm: 'nav.warroom' },
  ] },
  { group: 'Trading', items: [
    { path: '/portfolio', label: 'Portfolio', icon: Briefcase, perm: 'nav.portfolio' },
    { path: '/positions', label: 'Positions', icon: Target, perm: 'nav.positions' },
    { path: '/scanner', label: 'Scanner', icon: Search, perm: 'nav.scanner' },
    { path: '/stocks', label: 'All Stocks', icon: TrendingUp, perm: 'nav.stocks' },
    { path: '/options', label: 'Options', icon: Layers, perm: 'nav.options' },
    { path: '/crypto', label: 'Crypto', icon: Bitcoin, perm: 'nav.crypto' },
  ] },
  { group: 'Intelligence', items: [
    { path: '/agents', label: 'AI Agents', icon: Zap, perm: 'nav.agents' },
    { path: '/backtest', label: 'Backtest', icon: BarChart3, perm: 'nav.backtest' },
    { path: '/performance', label: 'Performance', icon: Activity, perm: 'nav.performance' },
    { path: '/scorecard', label: 'AI Scorecard', icon: ShieldCheck, perm: 'nav.scorecard' },
    { path: '/strategies', label: 'Strategies', icon: Compass, perm: 'nav.strategies' },
  ] },
  { group: 'Research', items: [
    { path: '/news', label: 'News', icon: Newspaper, perm: 'nav.news' },
    { path: '/gurus', label: 'Gurus', icon: Award, perm: 'nav.gurus' },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, perm: 'nav.leaderboard' },
  ] },
  { group: 'System', items: [
    { path: '/admin', label: 'Bot Control', icon: Bot, perm: 'nav.botcontrol' },
    { path: '/journal', label: 'Journal', icon: BookOpen, perm: 'nav.journal' },
    { path: '/settings', label: 'Settings', icon: Settings, perm: 'nav.settings' },
    { path: '/help', label: 'Help & Guide', icon: LifeBuoy, perm: null },
    { path: '/platform-admin', label: 'Platform Admin', icon: Shield, perm: 'nav.platformadmin' },
  ] },
];

const FLAT = NAV.flatMap((g) => g.items);

export default function AppShell({ children }) {
  const { user, logout, can, hasAlpacaKeys } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Persistent top-bar status: market clock + Alpaca account. Refreshed every 60s.
  const [clock, setClock] = useState(null);
  const [account, setAccount] = useState(null);
  const [alpacaConnected, setAlpacaConnected] = useState(null); // null = unknown yet

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [clockRes, acctRes] = await Promise.all([
        alpacaAPI.getMarketClock().catch(() => null),
        hasAlpacaKeys ? alpacaAPI.getAccount().catch(() => null) : Promise.resolve(null),
      ]);
      if (!alive) return;
      if (clockRes?.data) setClock(clockRes.data);
      if (acctRes?.data) { setAccount(acctRes.data); setAlpacaConnected(true); }
      else if (hasAlpacaKeys) setAlpacaConnected(false);
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [hasAlpacaKeys]);

  const dayPnl = account?.dayPnl;

  const path = location.pathname === '/' ? '/dashboard' : location.pathname;
  const active = FLAT.find((i) => path === i.path || path.startsWith(i.path + '/'));
  const title = active ? active.label : 'Paeru Exchange';
  const initials = (user?.username || user?.displayName || 'U').slice(0, 2).toUpperCase();

  const handleLogout = () => { try { logout && logout(); } finally { navigate('/login'); } };

  return (
    <div className="shell">
      {/* Sidebar */}
      <aside className="shell-side">
        <div className="flex items-center gap-3 px-5 pt-5 pb-4">
          <div className="w-[34px] h-[34px] rounded-[10px] grid place-items-center flex-none"
               style={{ background: 'linear-gradient(140deg,#E7B24C,#C0902E)', boxShadow: '0 3px 18px rgba(231,178,76,.35), inset 0 1px 0 rgba(255,255,255,.25)' }}>
            <TrendingUp className="w-[18px] h-[18px]" style={{ color: '#2b1c04' }} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif text-[20px] font-semibold leading-none tracking-tight">
              P<em className="not-italic" style={{ color: '#E7B24C', fontStyle: 'italic' }}>ae</em>ru
            </div>
            <span className="block font-mono text-[9px] tracking-[.18em] text-text-muted uppercase mt-1">Aramaic · money</span>
          </div>
        </div>

        <nav className="shell-nav">
          {NAV.map((g) => {
            // Show items with no perm (e.g. Help) to everyone; gate the rest by role.
            const items = g.items.filter((it) => !it.perm || can(it.perm));
            if (!items.length) return null;
            return (
              <div key={g.group}>
                <div className="shell-glabel">{g.group}</div>
                {items.map((it) => {
                  const isActive = path === it.path || path.startsWith(it.path + '/');
                  const Icon = it.icon;
                  return (
                    <a key={it.path} className={`shell-link${isActive ? ' active' : ''}`} onClick={() => navigate(it.path)}>
                      <Icon className="w-4 h-4 flex-none opacity-90" strokeWidth={2} />
                      <span>{it.label}</span>
                      {it.tag && <span className={`ml-auto font-mono text-[9px] font-semibold px-[7px] py-[2px] rounded-full uppercase tracking-wide ${it.tagCls}`}>{it.tag}</span>}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border-color p-3 flex flex-col gap-[11px]">
          <div className="flex items-center gap-[11px]">
            <div className="w-[34px] h-[34px] rounded-[10px] grid place-items-center flex-none font-mono font-bold text-[13px]"
                 style={{ background: 'linear-gradient(140deg,#2a2416,#1a1d2a)', color: '#E7B24C', border: '1px solid rgba(231,178,76,.25)' }}>{initials}</div>
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold truncate">{user?.username || 'Account'}</div>
              <div className="font-mono text-[10.5px] text-text-muted uppercase">Paper trading</div>
            </div>
          </div>
          <button onClick={handleLogout}
                  className="flex items-center justify-center gap-2 py-[9px] px-[10px] border border-border-color rounded-[9px] text-text-secondary text-[12.5px] font-semibold transition-colors hover:text-accent-red hover:border-accent-red/50"
                  style={{ background: 'none' }} title="Sign out">
            <LogOut className="w-[15px] h-[15px]" strokeWidth={2} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="shell-main">
        <header className="shell-top">
          <h1 className="font-serif text-[22px] font-semibold tracking-tight">{title}</h1>
          <div className="flex-1" />
          <div className="flex items-center gap-2 mr-1 overflow-x-auto">
            {/* Market open/closed */}
            <StatusChip
              color={clock ? (clock.isOpen ? 'green' : 'red') : 'muted'}
              pulse={!!clock?.isOpen}
              label={clock ? (clock.isOpen ? 'Market Open' : 'Market Closed') : 'Market —'}
            />
            {/* Alpaca connection */}
            {hasAlpacaKeys && (
              <StatusChip
                color={alpacaConnected == null ? 'muted' : alpacaConnected ? 'green' : 'red'}
                label={alpacaConnected == null ? 'Alpaca …' : alpacaConnected ? 'Alpaca Connected' : 'Alpaca Offline'}
              />
            )}
            {/* Buying power + day P&L */}
            {account && <MetricChip label="BP" value={money(account.buyingPower)} />}
            {account && (
              <MetricChip
                label="Day P&L"
                value={`${dayPnl >= 0 ? '+' : ''}${money(dayPnl)}`}
                valueClass={dayPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}
              />
            )}
          </div>
          <button onClick={() => window.location.reload()} title="Refresh"
                  className="w-[35px] h-[35px] grid place-items-center rounded-[9px] border border-border-color bg-bg-secondary text-text-secondary hover:text-text-primary hover:border-[#333A50] transition-colors flex-none">
            <RefreshCw className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>
        <div className="shell-content">{children}</div>
      </div>

      {/* First-run onboarding (shows once until dismissed or fully set up) */}
      <WelcomeModal />
    </div>
  );
}
